/**
 * MeshCore companion client: talks the real protocol over any transport.
 *
 * Transport = something that moves whole frames (BLE NUS in the browser, a
 * framed serial port on a base station, a fake in tests). The client
 * serializes commands (the companion answers in order, one reply per
 * command), routes unprompted pushes to listeners, and pulls queued channel
 * messages when the radio says some are waiting.
 *
 * Lessons ported from MeshBell (bench-proven on a Heltec V4):
 *  - OK on send means QUEUED, not radiated: `sendChannelText` confirms the
 *    frame hit the air by watching the firmware's own flood_tx counter.
 *  - Channel text over the budget is truncated by the firmware while it still
 *    reports OK, so we refuse it instead.
 *  - SET_RADIO_PARAMS is only trustworthy as the first command of a fresh
 *    connection, verified by reconnecting; this class never pretends otherwise.
 */

import {
  type ChannelInfo,
  type ChannelMessage,
  type CompanionFrame,
  type DeviceInfo,
  type PacketStats,
  type RadioParams,
  type SelfInfo,
  CHANNEL_SECRET_LEN,
  STATS_TYPE,
  channelTextBudget,
  decodeFrame,
  encodeAppStart,
  encodeChannelText,
  encodeDeviceQuery,
  encodeGetChannel,
  encodeGetStats,
  encodeNodeDiscover,
  encodeSetChannel,
  encodeSetRadio,
  encodeSyncNextMessage,
  isPush,
} from './companion'

export interface CompanionTransport {
  send(frame: Uint8Array): Promise<void>
  /** Register the single frame sink. Called once by the client. */
  onFrame(cb: (frame: Uint8Array) => void): void
  close(): Promise<void>
}

export class CompanionError extends Error {
  constructor(message: string, readonly code?: number) {
    super(message)
    this.name = 'CompanionError'
  }
}

export interface SendResult {
  /** The radio accepted the command. */
  queued: boolean
  /** The firmware's flood_tx counter moved: the frame provably went on air. */
  radiated: boolean
  /** Milliseconds from send to radiated (or to giving up). */
  elapsedMs: number
}

type FrameKind = CompanionFrame['kind']

type Pending = {
  expect: readonly FrameKind[]
  resolve: (f: CompanionFrame) => void
  reject: (e: Error) => void
  timer: ReturnType<typeof setTimeout>
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

export class MeshCoreCompanion {
  selfInfo: SelfInfo | null = null
  deviceInfo: DeviceInfo | null = null

  private queue: Promise<unknown> = Promise.resolve()
  private pending: Pending | null = null
  private channelListeners = new Set<(m: ChannelMessage) => void>()
  private pushListeners = new Set<(f: CompanionFrame) => void>()
  private syncing = false
  /** Non-push frames that matched no pending command (e.g. transport duplicates). */
  strayFrames = 0

  constructor(
    private readonly transport: CompanionTransport,
    private readonly opts: { commandTimeoutMs?: number; autoSync?: boolean } = {}
  ) {
    transport.onFrame((raw) => this.handleFrame(raw))
  }

  // ----- lifecycle -----

  /** APP_START + DEVICE_QUERY. Must be the first thing on a connection. */
  async start(appName = 'crowdtalkie'): Promise<SelfInfo> {
    const reply = await this.command(encodeAppStart(appName), ['self_info'])
    if (reply.kind !== 'self_info') throw this.unexpected('APP_START', reply)
    this.selfInfo = reply.info
    const dev = await this.command(encodeDeviceQuery(), ['device_info'])
    if (dev.kind === 'device_info') this.deviceInfo = dev.info
    return reply.info
  }

  async close(): Promise<void> {
    if (this.pending) {
      clearTimeout(this.pending.timer)
      this.pending.reject(new CompanionError('connection closed'))
      this.pending = null
    }
    await this.transport.close()
  }

  // ----- radio -----

  /** Queue new radio params. Only meaningful right after start(); reconnect to verify. */
  async setRadio(params: RadioParams): Promise<void> {
    await this.expectOk('SET_RADIO_PARAMS', encodeSetRadio(params))
  }

  // ----- channels -----

  async getChannel(index: number): Promise<ChannelInfo | null> {
    const reply = await this.command(encodeGetChannel(index), ['channel_info'])
    if (reply.kind === 'channel_info') return reply.info
    if (reply.kind === 'error') return null
    throw this.unexpected('GET_CHANNEL', reply)
  }

  async setChannel(index: number, name: string, secret: Uint8Array): Promise<void> {
    await this.expectOk('SET_CHANNEL', encodeSetChannel(index, name, secret))
  }

  /** Blank a slot: empty name, all-zero secret. */
  async clearChannel(index: number): Promise<void> {
    await this.setChannel(index, '', new Uint8Array(CHANNEL_SECRET_LEN))
  }

  /**
   * Find the slot already holding `secret`, or claim the first empty slot.
   * Slot 0 (the public channel) is never touched, nor any slot in use.
   */
  async ensureChannel(name: string, secret: Uint8Array): Promise<{ index: number; created: boolean }> {
    const max = this.deviceInfo?.maxChannels ?? 8
    let firstEmpty = -1
    for (let i = 1; i < max; i++) {
      const ch = await this.getChannel(i)
      if (!ch) break
      if (equalBytes(ch.secret, secret)) return { index: i, created: false }
      if (firstEmpty < 0 && ch.name === '' && ch.secret.every((b) => b === 0)) firstEmpty = i
    }
    if (firstEmpty < 0) throw new CompanionError('no free channel slot on this radio')
    await this.setChannel(firstEmpty, name, secret)
    return { index: firstEmpty, created: true }
  }

  // ----- text -----

  /** Bytes of text one channel message can carry on this radio. */
  textBudget(): number {
    if (!this.selfInfo) throw new CompanionError('call start() first')
    return channelTextBudget(this.selfInfo.name)
  }

  /**
   * Send channel text and confirm it radiated (flood_tx moved). Refuses text
   * the firmware would silently truncate.
   */
  async sendChannelText(channelIndex: number, text: string, opts: { confirmTimeoutMs?: number } = {}): Promise<SendResult> {
    const bytes = new TextEncoder().encode(text).length
    const budget = this.textBudget()
    if (bytes > budget) {
      throw new CompanionError(`text is ${bytes} bytes; this radio can send ${budget} (node name "${this.selfInfo?.name}")`)
    }
    const before = await this.packetStats()
    const t0 = Date.now()
    await this.expectOk('SEND_CHANNEL_TXT_MSG', encodeChannelText(channelIndex, text, Math.floor(Date.now() / 1000)))

    const deadline = t0 + (opts.confirmTimeoutMs ?? 15000)
    while (Date.now() < deadline) {
      await sleep(250)
      const now = await this.packetStats()
      if (now.floodTx > before.floodTx) return { queued: true, radiated: true, elapsedMs: Date.now() - t0 }
    }
    return { queued: true, radiated: false, elapsedMs: Date.now() - t0 }
  }

  /** Zero-hop "who's there?" on the current channel; replies arrive as pushes. */
  async discoverNodes(tag = Math.floor(Math.random() * 0xffffffff)): Promise<void> {
    await this.expectOk('NODE_DISCOVER', encodeNodeDiscover(tag))
  }

  async packetStats(): Promise<PacketStats> {
    const reply = await this.command(encodeGetStats(STATS_TYPE.PACKETS), ['stats_packets'])
    if (reply.kind !== 'stats_packets') throw this.unexpected('GET_STATS packets', reply)
    return reply.stats
  }

  /** Pull every queued message off the radio (listeners get channel ones). */
  async syncMessages(): Promise<number> {
    if (this.syncing) return 0
    this.syncing = true
    let n = 0
    try {
      for (;;) {
        const reply = await this.command(encodeSyncNextMessage(), ['channel_msg', 'no_more_msgs', 'other'])
        if (reply.kind === 'no_more_msgs') break
        if (reply.kind === 'channel_msg') {
          n++
          for (const l of this.channelListeners) l(reply.msg)
        } else if (reply.kind === 'error') {
          break
        }
        // contact (direct) messages are pulled and ignored for now
      }
    } finally {
      this.syncing = false
    }
    return n
  }

  onChannelMessage(cb: (m: ChannelMessage) => void): () => void {
    this.channelListeners.add(cb)
    return () => this.channelListeners.delete(cb)
  }

  onPush(cb: (f: CompanionFrame) => void): () => void {
    this.pushListeners.add(cb)
    return () => this.pushListeners.delete(cb)
  }

  // ----- plumbing -----

  /**
   * Send one command and resolve with its reply: the first frame whose kind
   * is in `expect`, or an ERROR. Commands never overlap; any other non-push
   * frame that turns up meanwhile is counted as stray, never taken as the answer.
   */
  command(frame: Uint8Array, expect: readonly FrameKind[]): Promise<CompanionFrame> {
    const run = () =>
      new Promise<CompanionFrame>((resolve, reject) => {
        const timer = setTimeout(() => {
          this.pending = null
          reject(new CompanionError(`no reply to command ${frame[0]} within ${this.timeoutMs()} ms`))
        }, this.timeoutMs())
        this.pending = { expect, resolve, reject, timer }
        this.transport.send(frame).catch((e) => {
          clearTimeout(timer)
          this.pending = null
          reject(e)
        })
      })
    const next = this.queue.then(run, run)
    this.queue = next.catch(() => undefined)
    return next
  }

  private timeoutMs(): number {
    return this.opts.commandTimeoutMs ?? 5000
  }

  private async expectOk(label: string, frame: Uint8Array): Promise<void> {
    const reply = await this.command(frame, ['ok'])
    if (reply.kind === 'ok') return
    if (reply.kind === 'error') throw new CompanionError(`${label} refused by radio`, reply.code)
    throw this.unexpected(label, reply)
  }

  private unexpected(label: string, reply: CompanionFrame): CompanionError {
    return new CompanionError(`${label}: unexpected reply ${reply.kind}`)
  }

  private handleFrame(raw: Uint8Array): void {
    const frame = decodeFrame(raw)
    if (isPush(frame)) {
      for (const l of this.pushListeners) l(frame)
      if (frame.kind === 'push_messages_waiting' && this.opts.autoSync !== false) {
        void this.syncMessages().catch(() => undefined)
      }
      return
    }
    const p = this.pending
    if (!p || (frame.kind !== 'error' && !p.expect.includes(frame.kind))) {
      this.strayFrames++
      return
    }
    clearTimeout(p.timer)
    this.pending = null
    p.resolve(frame)
  }
}

function equalBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}
